import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  GiftOutlined,
  InstagramOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useGiveaways } from '../hooks/useGiveaways';
import {
  Giveaway,
  GiveawayStatus,
} from '../../api/services/giveawayService';

const { Title, Text, Paragraph } = Typography;
const ORANGE = '#fe5a1d';

const STATUSES: GiveawayStatus[] = ['DRAFT', 'ACTIVE', 'CLOSED', 'COMPLETED'];

const STATUS_COLORS: Record<GiveawayStatus, string> = {
  DRAFT: 'default',
  ACTIVE: 'green',
  CLOSED: 'orange',
  COMPLETED: 'blue',
};

/**
 * Mirrors the backend's findActiveGiveaway: ACTIVE, inside its date window,
 * newest first. Whatever this returns is exactly what users see in the app.
 */
function resolveLiveGiveaway(giveaways: Giveaway[]): Giveaway | null {
  const now = Date.now();
  const eligible = giveaways
    .filter((g) => g.status === 'ACTIVE')
    .filter((g) => !g.startsAt || new Date(g.startsAt).getTime() <= now)
    .filter((g) => !g.endsAt || new Date(g.endsAt).getTime() >= now)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  return eligible[0] ?? null;
}

const GiveawayManagement: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    giveaways,
    loading,
    modalVisible,
    editing,
    submitting,
    togglingId,
    setModalVisible,
    handleCreate,
    handleUpdate,
    handleToggleVisible,
    handleDelete,
    openCreateModal,
    openEditModal,
    refetch,
  } = useGiveaways();

  const live = useMemo(() => resolveLiveGiveaway(giveaways), [giveaways]);
  const activeCount = giveaways.filter((g) => g.status === 'ACTIVE').length;

  useEffect(() => {
    if (!modalVisible) return;
    if (editing) {
      form.setFieldsValue({
        title: editing.title,
        description: editing.description ?? '',
        prize: editing.prize ?? '',
        instagramPostUrl: editing.instagramPostUrl ?? '',
        instagramHandle: editing.instagramHandle ?? '',
        requiredTagCount: editing.requiredTagCount,
        rewardCredits: editing.rewardCredits,
        status: editing.status,
        startsAt: editing.startsAt ? dayjs(editing.startsAt) : null,
        endsAt: editing.endsAt ? dayjs(editing.endsAt) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        requiredTagCount: 3,
        rewardCredits: 0,
        status: 'DRAFT' as GiveawayStatus,
      });
    }
  }, [modalVisible, editing, form]);

  const onFinish = (values: {
    title: string;
    description?: string;
    prize?: string;
    instagramPostUrl?: string;
    instagramHandle?: string;
    requiredTagCount: number;
    rewardCredits: number;
    status: GiveawayStatus;
    startsAt?: dayjs.Dayjs | null;
    endsAt?: dayjs.Dayjs | null;
  }) => {
    const payload = {
      title: values.title,
      description: values.description || undefined,
      prize: values.prize || undefined,
      instagramPostUrl: values.instagramPostUrl || undefined,
      instagramHandle: values.instagramHandle || undefined,
      requiredTagCount: values.requiredTagCount,
      rewardCredits: values.rewardCredits,
      status: values.status,
      startsAt: values.startsAt ? values.startsAt.toISOString() : null,
      endsAt: values.endsAt ? values.endsAt.toISOString() : null,
    };
    if (editing) handleUpdate(editing.id, payload);
    else handleCreate(payload);
  };

  const columns = [
    {
      title: t('admin.giveaway.col_title'),
      dataIndex: 'title',
      key: 'title',
      render: (value: string, record: Giveaway) => (
        <Space direction="vertical" size={0}>
          <Space>
            <GiftOutlined style={{ color: ORANGE }} />
            <Text strong>{value}</Text>
            {live?.id === record.id && (
              <Tag color="green">{t('admin.giveaway.tag_live')}</Tag>
            )}
          </Space>
          {!!record.prize && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.prize}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title={t('admin.giveaway.col_visible_hint')}>
          {t('admin.giveaway.col_visible')}
        </Tooltip>
      ),
      key: 'visible',
      width: 130,
      render: (_: unknown, record: Giveaway) => (
        <Space>
          <Switch
            checked={record.status === 'ACTIVE'}
            loading={togglingId === record.id}
            onChange={(checked) => handleToggleVisible(record.id, checked)}
            checkedChildren={<EyeOutlined />}
            unCheckedChildren={<EyeInvisibleOutlined />}
          />
        </Space>
      ),
    },
    {
      title: t('admin.giveaway.col_status'),
      dataIndex: 'status',
      key: 'status',
      render: (value: GiveawayStatus) => (
        <Tag color={STATUS_COLORS[value]}>
          {t(`admin.giveaway.status_${value.toLowerCase()}`)}
        </Tag>
      ),
    },
    {
      title: t('admin.giveaway.col_entries'),
      dataIndex: 'entryCount',
      key: 'entryCount',
      render: (value: number) => <Text>{value ?? 0}</Text>,
    },
    {
      title: t('admin.giveaway.col_tags'),
      dataIndex: 'requiredTagCount',
      key: 'requiredTagCount',
      render: (value: number) => <Text>{value}</Text>,
    },
    {
      title: t('admin.giveaway.col_reward'),
      dataIndex: 'rewardCredits',
      key: 'rewardCredits',
      render: (value: number) =>
        value > 0 ? (
          <Text>{value}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('admin.giveaway.col_ends'),
      dataIndex: 'endsAt',
      key: 'endsAt',
      render: (value: string | null) =>
        value ? (
          <Text type="secondary">{new Date(value).toLocaleDateString()}</Text>
        ) : (
          <Text type="secondary">{t('admin.giveaway.no_end_date')}</Text>
        ),
    },
    {
      title: t('admin.giveaway.col_actions'),
      key: 'actions',
      render: (_: unknown, record: Giveaway) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record)}
            style={{ color: ORANGE }}
          />
          <Popconfirm
            title={t('admin.giveaway.delete_confirm')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('admin.giveaway.btn_delete')}
            okType="danger"
            cancelText={t('admin.giveaway.btn_cancel')}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('admin.giveaway.title')}
          </Title>
          <Text type="secondary">{t('admin.giveaway.subtitle')}</Text>
        </div>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={refetch}
            loading={loading}
            className="admin-filter-btn"
          >
            {t('admin.giveaway.btn_refresh')}
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
            className="ant-btn-primary admin-filter-btn admin-filter-btn-primary"
          >
            {t('admin.giveaway.btn_add')}
          </Button>
        </Space>
      </div>

      {/* What users actually see right now */}
      <Alert
        type={live ? 'success' : 'info'}
        showIcon
        icon={live ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        message={
          live
            ? t('admin.giveaway.banner_live', { title: live.title })
            : t('admin.giveaway.banner_hidden')
        }
        description={
          live
            ? t('admin.giveaway.banner_live_desc')
            : t('admin.giveaway.banner_hidden_desc')
        }
      />

      {/* Only the newest ACTIVE one is ever shown — make that explicit */}
      {activeCount > 1 && (
        <Alert
          type="warning"
          showIcon
          message={t('admin.giveaway.banner_multiple', {
            count: activeCount,
          })}
          description={t('admin.giveaway.banner_multiple_desc', {
            title: live?.title ?? '',
          })}
        />
      )}

      <Card bordered={false} className="admin-table-card">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={giveaways}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t('admin.giveaway.empty_text') }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <GiftOutlined style={{ color: 'var(--admin-primary)' }} />
            {editing
              ? t('admin.giveaway.modal_edit_title')
              : t('admin.giveaway.modal_add_title')}
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        onOk={() => form.submit()}
        okText={
          editing
            ? t('admin.giveaway.modal_save')
            : t('admin.giveaway.modal_create')
        }
        okButtonProps={{ className: 'ant-btn-primary' }}
        cancelText={t('admin.giveaway.btn_cancel')}
        width={620}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="title"
            label={t('admin.giveaway.label_title')}
            rules={[
              { required: true, message: t('admin.giveaway.validation_title') },
            ]}
          >
            <Input
              maxLength={255}
              placeholder={t('admin.giveaway.placeholder_title')}
            />
          </Form.Item>

          <Form.Item name="prize" label={t('admin.giveaway.label_prize')}>
            <Input
              maxLength={255}
              placeholder={t('admin.giveaway.placeholder_prize')}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('admin.giveaway.label_description')}
          >
            <Input.TextArea
              rows={3}
              maxLength={2000}
              placeholder={t('admin.giveaway.placeholder_description')}
            />
          </Form.Item>

          <Form.Item
            name="instagramPostUrl"
            label={t('admin.giveaway.label_post_url')}
            extra={t('admin.giveaway.hint_post_url')}
          >
            <Input
              maxLength={500}
              prefix={<InstagramOutlined />}
              placeholder="https://www.instagram.com/p/..."
            />
          </Form.Item>

          <Form.Item
            name="instagramHandle"
            label={t('admin.giveaway.label_handle')}
            extra={t('admin.giveaway.hint_handle')}
          >
            <Input maxLength={100} addonBefore="@" placeholder="autoplace.al" />
          </Form.Item>

          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item
              name="requiredTagCount"
              label={t('admin.giveaway.label_tags')}
              rules={[{ required: true }]}
              extra={t('admin.giveaway.hint_tags')}
            >
              <InputNumber min={0} max={20} style={{ width: 140 }} />
            </Form.Item>

            <Form.Item
              name="rewardCredits"
              label={t('admin.giveaway.label_reward')}
              rules={[{ required: true }]}
              extra={t('admin.giveaway.hint_reward')}
            >
              <InputNumber min={0} max={1000} style={{ width: 140 }} />
            </Form.Item>

            <Form.Item
              name="status"
              label={t('admin.giveaway.label_status')}
              rules={[{ required: true }]}
              extra={t('admin.giveaway.hint_status')}
            >
              <Select
                style={{ width: 180 }}
                options={STATUSES.map((s) => ({
                  value: s,
                  label: t(`admin.giveaway.status_${s.toLowerCase()}`),
                }))}
              />
            </Form.Item>
          </Space>

          <Space size="large" style={{ display: 'flex' }}>
            <Form.Item name="startsAt" label={t('admin.giveaway.label_starts')}>
              <DatePicker showTime style={{ width: 240 }} />
            </Form.Item>
            <Form.Item name="endsAt" label={t('admin.giveaway.label_ends')}>
              <DatePicker showTime style={{ width: 240 }} />
            </Form.Item>
          </Space>

          <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            {t('admin.giveaway.modal_footnote')}
          </Paragraph>
        </Form>
      </Modal>
    </Space>
  );
};

export default GiveawayManagement;
